// Light, dark or follow the system. The choice is kept per device (localStorage), and applied as
// <html data-theme="light|dark">, which dark.css keys off. public/theme-init.js applies it before the first
// paint so a dark page doesn't flash white; this keeps it up to date afterwards.
import { useSyncExternalStore } from "react";

export type ThemePref = "light" | "dark" | "system";

const KEY = "wt-theme"; // also read by public/theme-init.js
const DARK = "(prefers-color-scheme: dark)";
// The top bar's colour in each theme, for the phone's status bar (meta theme-color).
const BAR = { light: "#1d3887", dark: "#141d40" };

const listeners = new Set<() => void>();
let unsaved: ThemePref | null = null; // the choice, when storage is blocked (e.g. some private windows)

function stored(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

const themePref = (): ThemePref => unsaved ?? stored();

function apply() {
  const pref = themePref();
  const systemDark = typeof window.matchMedia === "function" && window.matchMedia(DARK).matches;
  const theme = pref === "dark" || (pref === "system" && systemDark) ? "dark" : "light";
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", BAR[theme]);
}

export function setThemePref(pref: ThemePref) {
  try {
    if (pref === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
    unsaved = null;
  } catch {
    unsaved = pref; // still applies until the page is closed
  }
  apply();
  listeners.forEach((l) => l());
}

/** Apply the saved theme, and follow the system's light/dark setting (for "system"). Call once, at start-up. */
export function startTheme() {
  apply();
  if (typeof window.matchMedia === "function") window.matchMedia(DARK).addEventListener("change", apply);
}

/** The chosen theme, re-rendering when it changes. */
export function useThemePref(): ThemePref {
  return useSyncExternalStore((l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  }, themePref);
}
